import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import VersionSelector, { VersionSelectorProps } from "@/components/VersionSelector";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNavigateWithSearch } from "@/hooks";
import {
  useCreatePromptLabel,
  useDynamicReleaseLabelsByPromptRegistry,
  useEvaluationScoresForVersions,
  usePromptLabel,
  usePromptLabelsDelete,
  usePromptLabelsPatch,
  usePromptVersions,
  useRequestCountsForVersions,
} from "@/queries";
import { useInfinitePromptLabels } from "@/queries/prompt-labels";
import { displayErrorToast } from "@/utils/toast";

const PromptTemplateVersionSelector = ({
  selectedVersionId,
  ...versionSelectorProps
}: {
  selectedVersionId?: number;
} & Partial<VersionSelectorProps>) => {
  const { promptId, versionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const labelParam = searchParams.get("label");
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useAuth()?.userToken ?? "";
  const navigateWithSearch = useNavigateWithSearch();
  const queryClient = useQueryClient();
  const numericPromptId = Number(promptId!);
  const addReleaseLabel = useCreatePromptLabel(userToken);
  const deleteReleaseLabel = usePromptLabelsDelete(userToken);
  const patchReleaseLabel = usePromptLabelsPatch(userToken);
  const getPromptLabel = usePromptLabel();
  const promptVersionsQuery = usePromptVersions(userToken, {
    promptRegistryId: numericPromptId,
    workspaceId,
  });
  const promptLabelsQuery = useInfinitePromptLabels({
    userToken,
    workspace_id: workspaceId,
    prompt_id: numericPromptId,
    per_page: 100,
    enabled: !!workspaceId && !!numericPromptId,
  });

  const { data } = useDynamicReleaseLabelsByPromptRegistry(userToken, promptId!);
  const dynamicReleaseLabels = data?.dynamic_release_labels;

  const dynamicReleaseLabelRegistry: Record<number, { name: string; id: number }[]> =
    dynamicReleaseLabels
      ? Object.keys(dynamicReleaseLabels).reduce(
          (acc, vId) => {
            const intVersionId = Number(vId);
            acc[intVersionId] = dynamicReleaseLabels[Number(vId)].map(item => ({
              name: item.prompt_label_name,
              id: item.release_label_group_id,
            }));
            return acc;
          },
          {} as Record<number, { name: string; id: number }[]>,
        )
      : {};

  const versions = promptVersionsQuery.data?.pages.flatMap(page => page.prompt_versions);
  const promptLabels = useMemo(
    () => promptLabelsQuery.data?.pages.flatMap(page => page.prompt_labels) ?? [],
    [promptLabelsQuery.data?.pages],
  );
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = promptVersionsQuery;
  const {
    fetchNextPage: fetchNextLabelsPage,
    hasNextPage: hasNextLabelsPage,
    isFetchingNextPage: isFetchingNextLabelsPage,
    isLoading: isLoadingPromptLabels,
  } = promptLabelsQuery;
  const buildPromptVersionPath = useCallback(
    (version: number) => `/workspace/${workspaceId}/prompt/${promptId}/version/${version}`,
    [promptId, workspaceId],
  );

  const invalidateAfterLabelChange = useCallback(() => {
    promptVersionsQuery.refetch();
    promptLabelsQuery.refetch();
    queryClient.invalidateQueries({
      queryKey: ["global-changelog", workspaceId.toString()],
    });
    queryClient.invalidateQueries({ queryKey: ["changelog"] });
    queryClient.invalidateQueries({
      queryKey: ["changelog-pending-count", workspaceId.toString()],
    });
  }, [promptLabelsQuery, promptVersionsQuery, queryClient, workspaceId]);

  useEffect(() => {
    if (hasNextLabelsPage && !isFetchingNextLabelsPage) {
      fetchNextLabelsPage();
    }
  }, [fetchNextLabelsPage, hasNextLabelsPage, isFetchingNextLabelsPage]);

  useEffect(() => {
    if (!versionId || !versions) {
      return;
    }

    const activeVersionNumber = Number(versionId);
    if (Number.isNaN(activeVersionNumber) || versions.some(v => v.number === activeVersionNumber)) {
      return;
    }

    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, versionId, versions]);

  const defaultVersion = promptVersionsQuery.data?.pages?.[0]?.prompt_versions[0]?.number;
  const labelFromQuery = useMemo(
    () => (labelParam ? promptLabels.find(label => label.name === labelParam) : undefined),
    [labelParam, promptLabels],
  );

  const { data: requestCounts } = useRequestCountsForVersions(
    promptId!,
    workspaceId.toString(),
    userToken || "",
  );

  useEffect(() => {
    if (versionId || !versions) {
      return;
    }

    if (labelParam) {
      if (isLoadingPromptLabels || hasNextLabelsPage || isFetchingNextLabelsPage) {
        return;
      }

      if (labelFromQuery) {
        const versionWithLabel = versions.find(v => v.id === labelFromQuery.prompt_version_id);
        if (versionWithLabel) {
          const nextSearchParams = new URLSearchParams(searchParams);
          nextSearchParams.delete("label");
          setSearchParams(nextSearchParams, { replace: true });

          navigateWithSearch(buildPromptVersionPath(versionWithLabel.number), {
            replace: true,
            search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
          });
          return;
        }

        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
        return;
      }
    }

    if (defaultVersion !== undefined) {
      navigateWithSearch(buildPromptVersionPath(defaultVersion), { replace: true });
    }
  }, [
    buildPromptVersionPath,
    defaultVersion,
    fetchNextPage,
    hasNextLabelsPage,
    hasNextPage,
    isFetchingNextLabelsPage,
    isFetchingNextPage,
    isLoadingPromptLabels,
    labelFromQuery,
    labelParam,
    navigateWithSearch,
    searchParams,
    setSearchParams,
    versionId,
    versions,
  ]);

  const versionEvaluationScores = useEvaluationScoresForVersions(promptId!, userToken || "");

  return (
    <VersionSelector
      versions={versions}
      counts={requestCounts}
      versionEvaluationScoresRegistry={versionEvaluationScores.data}
      dynamicReleaseLabelsRegistry={dynamicReleaseLabelRegistry}
      refreshVersions={() => {
        promptVersionsQuery.refetch();
        promptLabelsQuery.refetch();
      }}
      onMoveReleaseLabel={(labelId, labelName, vId) =>
        new Promise((resolve, reject) => {
          patchReleaseLabel.mutate(
            {
              promptId: numericPromptId,
              name: labelName,
              versionId: Number(vId),
              id: labelId,
            },
            {
              onSuccess: responseData => {
                invalidateAfterLabelChange();
                resolve(responseData);
              },
              onError: error => {
                reject(error);
              },
            },
          );
        })
      }
      onAddReleaseLabel={async (label, vId) => {
        let match = promptLabels.find(({ name }) => name === label);

        if (!match) {
          const fetchedLabel = await getPromptLabel.mutateAsync({
            prompt_id: numericPromptId,
            name: label,
            authToken: userToken,
          });
          if (fetchedLabel) {
            match = fetchedLabel;
          }
        }

        if (match) {
          const existingLabelId = match.id;
          return () =>
            patchReleaseLabel.mutate(
              {
                promptId: numericPromptId,
                versionId: Number(vId),
                name: label,
                id: existingLabelId,
              },
              {
                onSuccess: () => {
                  invalidateAfterLabelChange();
                },
              },
            );
        }

        addReleaseLabel.mutate(
          {
            promptId: numericPromptId,
            name: label,
            versionId: Number(vId),
          },
          {
            onSuccess: () => {
              invalidateAfterLabelChange();
            },
            onError: error => {
              displayErrorToast(error);
            },
          },
        );
        return false;
      }}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onRemoveReleaseLabel={async label => {
        deleteReleaseLabel.mutate(
          {
            promptId: numericPromptId,
            versionId: selectedVersionId!,
            id: label,
          },
          {
            onSuccess: () => {
              invalidateAfterLabelChange();
            },
            onError: error => {
              displayErrorToast(error);
            },
          },
        );
      }}
      setShowVersionList={() => undefined}
      navigateToVersionLink={buildPromptVersionPath}
      showVersionList={false}
      {...versionSelectorProps}
    />
  );
};

export default PromptTemplateVersionSelector;
